import { Ref, RefObject, memo } from 'react';
import React from 'react';
import { Message } from '../types/chat.types';
import { Copy } from 'lucide-react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cleanMarkdown } from '../utils/chatExport';
import { ArtifactLink } from './ArtifactLink';

interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
  streamingText?: string;
  agentStatus?: string | null;
  queuedMessage?: string | null;
  scrollContainerRef: Ref<HTMLDivElement>;
  bottomRef: RefObject<HTMLDivElement | null>;
}

// ─── Module-level markdown config ────────────────────────────────────────────
// These MUST stay out of the render body. Recreating the plugin array or the
// components map on every render gives them a new identity on every streamed
// chunk, which forces react-markdown to redo work it could otherwise skip.

const REMARK_PLUGINS = [remarkGfm];

// Shared table components — used both in direct markdown tables and in
// re-rendered ```markdown code fences that contain tables.
const tableComponents: Components = {
  table: ({ children }) => (
    <div className="overflow-x-auto my-4">
      <table className="border-collapse text-sm">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border border-slate-300 bg-slate-50 px-3 py-2 text-left font-semibold text-slate-700 whitespace-nowrap">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-slate-300 px-3 py-2 align-top text-slate-700">
      {children}
    </td>
  ),
  tr: ({ children }) => (
    <tr className="even:bg-slate-50">{children}</tr>
  ),
};

const assistantComponents: Components = {
  ...tableComponents,

  pre: ({ children }) => {
    // Detect ```markdown fenced blocks and re-render their content as actual
    // markdown. This is the correct semantic fix when the AI wraps a table in
    // a code fence.
    let markdownContent: string | null = null;

    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child)) {
        const codeEl = child as React.ReactElement<{
          className?: string;
          children?: React.ReactNode;
        }>;
        if (codeEl.props.className === 'language-markdown') {
          markdownContent = String(codeEl.props.children ?? '').trim();
        }
      }
    });

    if (markdownContent !== null) {
      // Re-render the raw markdown string — tables will now go through our
      // tableComponents and get overflow-x-auto.
      return (
        <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={tableComponents}>
          {markdownContent}
        </ReactMarkdown>
      );
    }

    // Regular code block — wrap in a scrollable container.
    return (
      <div className="overflow-x-auto my-2 w-full">
        <pre className="min-w-0">{children}</pre>
      </div>
    );
  },

  a: ({ href, children }) => {
    const isArtifact =
      !!href && !href.startsWith('http') && !href.startsWith('/') && !href.startsWith('#');
    if (isArtifact) {
      return <ArtifactLink fileId={href}>{children}</ArtifactLink>;
    }
    return (
      <a
        href={href}
        className="text-blue-500 underline hover:text-blue-700"
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  },
};

// The in-flight bubble keeps the simpler `pre` (no ```markdown unwrapping):
// a fence that is still being streamed is incomplete by definition.
const streamingComponents: Components = {
  ...tableComponents,
  pre: ({ children }) => (
    <div className="overflow-x-auto my-2 w-full">
      <pre className="min-w-0">{children}</pre>
    </div>
  ),
};

// ─── One settled message ─────────────────────────────────────────────────────
// memo() is what keeps streaming cheap: `messages` keeps its identity while a
// response streams in (the reducer only replaces `streamingText`), so every
// already-rendered message here skips re-rendering — and, crucially, skips
// re-parsing its markdown — on every single chunk. Without this, a 10-message
// conversation re-parsed all 10 messages a few hundred times per response,
// starving the main thread and making the auto-scroll stutter.

const MessageRow = memo(({ message }: { message: Message }) => {
  if (message.role === 'user') {
    return (
      <div className="w-full flex justify-end">
        <div className="bg-gray-100 text-gray-800 text-base rounded-xl px-4 py-4 max-w-[80%]">
          <ReactMarkdown>{message.content}</ReactMarkdown>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex justify-start">
      <div className="text-gray-800 rounded-3xl px-0 md:px-6 py-6 markdown-content relative w-full min-w-0">
        <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={assistantComponents}>
          {message.content}
        </ReactMarkdown>

        <div className="left-0 bottom-0 flex items-center gap-2 pt-6 border-t w-full">
          <button
            className="p-1 rounded hover:bg-gray-200 transition"
            title="Copy reply"
            onClick={() => {
              navigator.clipboard.writeText(cleanMarkdown(message.content));
            }}
          >
            <Copy className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>
    </div>
  );
});
MessageRow.displayName = 'MessageRow';

export const MessageList = ({ messages, isLoading, streamingText, agentStatus, queuedMessage, scrollContainerRef, bottomRef }: MessageListProps) => {
  return (
    // overflow-x-hidden backstop: prevents the scroll container itself from
    // growing horizontally if any child overflows.
    <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 md:px-6 py-4">
      <div className="space-y-4">
        {messages.map((message, index) => (
          <MessageRow key={index} message={message} />
        ))}

        {isLoading && (
          <div className="flex flex-col gap-2 justify-start">
            {/* Mascot row — queue state or normal agent status */}
            {queuedMessage ? (
              <div className="flex items-center gap-3 text-amber-600">
                <img
                  src="/mascot-360-2.gif"
                  alt="Waiting in queue..."
                  className="h-12 w-12 flex-shrink-0"
                />
                <p className="text-base text-amber-500">{queuedMessage}</p>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <img
                  src="/mascot-360-2.gif"
                  alt="Thinking..."
                  className="h-12 w-12 flex-shrink-0"
                />
                {agentStatus && (
                  <span className="text-sm text-gray-400 italic">{agentStatus}</span>
                )}
              </div>
            )}

            {/* Streaming text bubble */}
            {streamingText && (
              <div className="text-gray-800 rounded-3xl  px-0 md:px-6 py-6 markdown-content w-full min-w-0">
                <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={streamingComponents}>
                  {streamingText}
                </ReactMarkdown>
                <span className="inline-block w-[2px] h-[1em] bg-gray-500 ml-0.5 animate-pulse align-middle" />
              </div>
            )}
          </div>
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
};
